import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback
} from "reactstrap";
import * as moment from "moment";
import { Link } from "react-router-dom";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { isEmpty } from "lodash";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import Loader from "../../../Components/Common/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { getOrders, updateOrder } from "../../../api/ecommerce";

// Map API order to table format
function mapOrderToRow(apiOrder) {
  const addr = typeof apiOrder.shipping_address === 'string' ? JSON.parse(apiOrder.shipping_address || '{}') : (apiOrder.shipping_address || {});
  const items = typeof apiOrder.items === 'string' ? JSON.parse(apiOrder.items || '[]') : (apiOrder.items || []);
  const productLabel = items.length > 0 ? (items[0].name + (items.length > 1 ? ` +${items.length - 1} more` : '')) : '-';
  const paymentLabel = apiOrder.payment_method === 'cod' ? 'Cash on Delivery' : 'Online Payment (Stripe)';
  return {
    _id: apiOrder.id,
    orderId: apiOrder.order_number,
    customer: addr.fullName || apiOrder.email || '-',
    product: productLabel,
    orderDate: apiOrder.created_at,
    amount: '£' + (Number(apiOrder.total) || 0).toFixed(2),
    payment: paymentLabel,
    status: apiOrder.order_status || 'pending',
    apiOrder,
  };
}

const EcommerceOrders = () => {
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [orderList, setOrderList] = useState([]);
  const [order, setOrder] = useState(null);
  const [isExportCSV, setIsExportCSV] = useState(false);

  const orderstatus = [
    {
      options: [
        { label: "Status", value: "Status" },
        { label: "All", value: "All" },
        { label: "Pending", value: "Pending" },
        { label: "Inprogress", value: "Inprogress" },
        { label: "Cancelled", value: "Cancelled" },
        { label: "Pickups", value: "Pickups" },
        { label: "Returns", value: "Returns" },
        { label: "Delivered", value: "Delivered" },
      ],
    },
  ];

  const cancelledReasons = [
    "Customer requested cancellation",
    "Ordered by mistake",
    "Found a better price elsewhere",
    "Delivery time too long",
    "Payment failure",
    "Out of stock",
    "Duplicate order",
    "Other (with comments)"
  ];

  const returnedReasons = [
    "Damaged product received",
    "Wrong item delivered",
    "Product not as described",
    "Missing parts/accessories",
    "Quality issues",
    "Size/fit issue",
    "Defective product",
    "Customer changed mind",
    "Other (with comments)"
  ];

  const orderpayement = [
    {
      options: [
        { label: "Select Payment", value: "Select Payment" },
        { label: "All", value: "All" },
        { label: "Mastercard", value: "Mastercard" },
        { label: "Paypal", value: "Paypal" },
        { label: "Visa", value: "Visa" },
        { label: "COD", value: "COD" },
      ],
    },
  ];

  const productname = [
    {
      options: [
        { label: "Product", value: "Product" },
        { label: "Puma Tshirt", value: "Puma Tshirt" },
        { label: "Adidas Sneakers", value: "Adidas Sneakers" },
        {
          label: "350 ml Glass Grocery Container",
          value: "350 ml Glass Grocery Container",
        },
        {
          label: "American egale outfitters Shirt",
          value: "American egale outfitters Shirt",
        },
        { label: "Galaxy Watch4", value: "Galaxy Watch4" },
        { label: "Apple iPhone 12", value: "Apple iPhone 12" },
        { label: "Funky Prints T-shirt", value: "Funky Prints T-shirt" },
        {
          label: "USB Flash Drive Personalized with 3D Print",
          value: "USB Flash Drive Personalized with 3D Print",
        },
        {
          label: "Oxford Button-Down Shirt",
          value: "Oxford Button-Down Shirt",
        },
        {
          label: "Classic Short Sleeve Shirt",
          value: "Classic Short Sleeve Shirt",
        },
        {
          label: "Half Sleeve T-Shirts (Blue)",
          value: "Half Sleeve T-Shirts (Blue)",
        },
        { label: "Noise Evolve Smartwatch", value: "Noise Evolve Smartwatch" },
      ],
    },
  ];

  const [isEdit, setIsEdit] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  // Single delete
  const onClickDelete = (order) => {
    setOrder(order);
    setDeleteModal(true);
  };

  const handleDeleteOrder = () => {
    if (order) {
      setOrderList(prev => prev.filter(item => item._id !== order._id));
      setDeleteModal(false);
      toast.success("Order deleted successfully");
    }
  };

  useEffect(() => {
    getOrders()
      .then((res) => {
        const data = Array.isArray(res) ? res : (res?.data || []);
        setOrderList(data.map(mapOrderToRow));
        setFetchError(null);
      })
      .catch((err) => {
        setFetchError(err?.message || 'Failed to load orders');
        setOrderList([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      getOrders()
        .then((res) => {
          const data = Array.isArray(res) ? res : (res?.data || []);
          let filtered = data.map(mapOrderToRow);
          if (type !== "all") {
            filtered = filtered.filter((o) => (o.status || '').toLowerCase() === (type || '').toLowerCase());
          }
          setOrderList(filtered);
        })
        .catch(() => {});
    }
  };

  // Formik validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      orderId: (order && order.orderId) || '',
      customer: (order && order.customer) || '',
      product: (order && order.product) || '',
      orderDate: (order && order.orderDate) || '',
      amount: (order && order.amount) || '',
      payment: (order && order.payment) || '',
      status: (order && order.status) || '',
      status_reason: (order && order.status_reason) || '',
      status_reason_other: ''
    },
    validationSchema: Yup.object({
      orderId: Yup.string().required("Please Enter order Id"),
      customer: Yup.string().required("Please Enter Customer Name"),
      product: Yup.string().required("Please Enter Product Name"),
      amount: Yup.string().required("Please Enter Total Amount"),
      payment: Yup.string().required("Please Enter Payment Method"),
      status: Yup.string().required("Please Enter Delivery Status")
    }),
    onSubmit: (values) => {
      if (isEdit && order?.apiOrder?.order_number) {
        let finalReason = null;
        if (values.status === "Cancelled" || values.status === "Returns") {
          finalReason = values.status_reason === "Other (with comments)" ? values.status_reason_other : values.status_reason;
        }

        updateOrder(order.apiOrder.order_number, { 
          order_status: values.status,
          status_reason: finalReason
        })
          .then((res) => {
            const updated = res?.data || res;
            setOrderList(prev =>
              prev.map(item => item.orderId === order.orderId
                ? { ...item, status: updated.order_status || values.status }
                : item
              )
            );
            toast.success("Order updated");
            validation.resetForm();
            toggle();
          })
          .catch((e) => toast.error(e?.message || "Failed to update"));
        return;
      }
      if (isEdit) {
        const updateOrderLocal = {
          _id: order ? order._id : 0,
          orderId: values.orderId,
          customer: values.customer,
          product: values.product,
          orderDate: date,
          amount: values.amount,
          payment: values.payment,
          status: values.status,
          status_reason: (values.status === "Cancelled" || values.status === "Returns") ? (values.status_reason === "Other (with comments)" ? values.status_reason_other : values.status_reason) : null
        };
        setOrderList(prev =>
          prev.map(item => item._id === order._id ? updateOrderLocal : item)
        );
        toast.success("Order updated");
        validation.resetForm();
      } else {
        const newOrder = {
          _id: (Math.floor(Math.random() * 10000)).toString(),
          orderId: values["orderId"],
          customer: values["customer"],
          product: values["product"],
          orderDate: date,
          amount: values["amount"],
          payment: values["payment"],
          status: values["status"]
        };
        setOrderList(prev => [newOrder, ...prev]);
        toast.success("Order added");
        validation.resetForm();
      }
      toggle();
    },
  });

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setOrder(null);
    } else {
      setModal(true);
      setDate(defaultdate());
    }
  }, [modal]);

  const handleOrderClicks = () => {
    setOrder("");
    setIsEdit(false);
    toggle();
  };

const handleOrderClick = useCallback((arg) => {
  const order = arg;
  setOrder({
    _id: order._id,
    orderId: order.orderId,
    customer: order.customer,
    product: order.product,
    orderDate: order.orderDate,
    amount: order.amount,
    payment: order.payment,
    status: order.status,
    apiOrder: order.apiOrder,
  });

  setIsEdit(true);
  toggle();
}, [toggle]);
  // Checked All
  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");

    if (checkall.checked) {
      ele.forEach((ele) => { ele.checked = true; });
    } else {
      ele.forEach((ele) => { ele.checked = false; });
    }
    deleteCheckbox();
  }, []);

  // Delete Multiple
  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = () => {
    const ids = selectedCheckBoxDelete.map(el => el.value);
    setOrderList(prev => prev.filter(item => !ids.includes(item._id)));
    document.getElementById("checkBoxAll").checked = false;
    setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete([]);
    toast.success("Selected orders deleted");
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".orderCheckBox:checked");
    ele.length > 0 ? setIsMultiDeleteButton(true) : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  // Columns
  const columns = useMemo(
    () => [
      {
        header: <input type="checkbox" id="checkBoxAll" className="form-check-input" onClick={() => checkedAll()} />,
        cell: (cell) => {
          return <input type="checkbox" className="orderCheckBox form-check-input" value={cell.getValue()} onChange={() => deleteCheckbox()} />;
        },
        id: '#',
        accessorKey: '_id',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Order Id",
        accessorKey: "orderId",
        enableColumnFilter: false,
        cell: (cell) => {
          return <Link to={`/order-details/${cell.row.original.apiOrder?.order_number || cell.row.original.orderId || cell.row.original._id}`} className="fw-medium link-primary">{cell.getValue()}</Link>;
        },
      },
      {
        header: "Customer",
        accessorKey: "customer",
        enableColumnFilter: false,
      },
      {
        header: "Product",
        accessorKey: "product",
        enableColumnFilter: false,
      },
      {
        header: "Order Date",
        accessorKey: "orderDate",
        enableColumnFilter: false,
        cell: (cell) => (
          <>
            {handleValidDate(cell.getValue())},
            <small className="text-muted"> {handleValidTime(cell.getValue())}</small>
          </>
        ),
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
      },
      {
        header: "Payment Method",
        accessorKey: "payment",
        enableColumnFilter: false,
      },
      {
        header: 'Delivery Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        cell: (cell) => {
          switch (cell.getValue()) {
            case "Pending":
              return <span className="badge text-uppercase bg-warning-subtle text-warning"> {cell.getValue()} </span>;
            case "Cancelled":
              return <span className="badge text-uppercase bg-danger-subtle text-danger"> {cell.getValue()} </span>;
            case "Inprogress":
              return <span className="badge text-uppercase bg-secondary-subtle text-secondary"> {cell.getValue()} </span>;
            case "Pickups":
              return <span className="badge text-uppercase bg-info-subtle text-info"> {cell.getValue()} </span>;
            case "Returns":
              return <span className="badge text-uppercase bg-primary-subtle text-primary"> {cell.getValue()} </span>;
            case "Delivered":
              return <span className="badge text-uppercase bg-success-subtle text-success"> {cell.getValue()} </span>;
            default:
              return <span className="badge text-uppercase bg-warning-subtle text-warning"> {cell.getValue()} </span>;
          }
        }
      },
      {
        header: "Action",
        cell: (cellProps) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item">
                <Link
                  to={`/order-details/${cellProps.row.original.apiOrder?.order_number || cellProps.row.original.orderId || cellProps.row.original._id}`}
                  className="text-primary d-inline-block"
                >
                  <i className="ri-eye-fill fs-16"></i>
                </Link>
              </li>
              <li className="list-inline-item edit">
                <Link
                  to="#"
                  className="text-primary d-inline-block edit-item-btn"
                  onClick={() => {
                    const orderData = cellProps.row.original;
                    handleOrderClick(orderData);
                  }}
                >
                  <i className="ri-pencil-fill fs-16"></i>
                </Link>
              </li>
              <li className="list-inline-item">
                <Link
                  to="#"
                  className="text-danger d-inline-block remove-item-btn"
                  onClick={() => {
                    const orderData = cellProps.row.original;
                    onClickDelete(orderData);
                  }}
                >
                  <i className="ri-delete-bin-5-fill fs-16"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleOrderClick, checkedAll]
  );

  // Date helpers
  const defaultdate = () => {
    let d = new Date(),
      months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let h = (d.getHours() % 12) || 12;
    let ampm = d.getHours() < 12 ? "AM" : "PM";
    return ((d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear() + ", " + h + ":" + d.getMinutes() + " " + ampm).toString());
  };

  const [date, setDate] = useState(defaultdate());

  const dateformate = (e) => {
    const dateString = e.toString().split(" ");
    let time = dateString[4];
    let H = +time.substr(0, 2);
    let h = (H % 12) || 12;
    h = (h <= 9) ? ("0" + h) : h;
    let ampm = H < 12 ? "AM" : "PM";
    time = h + time.substr(2, 3) + " " + ampm;
    const dateStr = dateString[2] + " " + dateString[1] + ", " + dateString[3];
    const orderDate = (dateStr + ", " + time).toString();
    setDate(orderDate);
  };

  const handleValidDate = date => moment(new Date(date)).format("DD MMM Y");
  const handleValidTime = time => {
    const t = new Date(time);
    const h = t.getHours();
    const m = t.getMinutes();
    const meridiem = h >= 12 ? "PM" : "AM";
    return moment(`${h}:${m}`, 'hh:mm').format('hh:mm') + " " + meridiem;
  };

  document.title = "Orders - Dashboard";

  return (
    <div className="page-content">
      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={orderList}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        <BreadCrumb title="Orders" pageTitle="Dashboard" />
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Order History</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button type="button" className="btn btn-info" onClick={() => setIsExportCSV(true)}>
                        <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                        Export
                      </button>
                      {" "}
                      {isMultiDeleteButton && <button className="btn btn-soft-danger"
                        onClick={() => setDeleteModalMulti(true)}
                      ><i
                        className="ri-delete-bin-2-line"></i></button>}
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                <div>
                  <Nav className="nav-tabs nav-tabs-custom nav-success" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" }, "fw-semibold")}
                        onClick={() => { toggleTab("1", "all"); }}
                        href="#"
                      >
                        <i className="ri-store-2-fill me-1 align-bottom"></i>{" "}
                        All Orders
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "2" }, "fw-semibold")}
                        onClick={() => { toggleTab("2", "Delivered"); }}
                        href="#"
                      >
                        <i className="ri-checkbox-circle-line me-1 align-bottom"></i>{" "}
                        Delivered
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "3" }, "fw-semibold")}
                        onClick={() => { toggleTab("3", "Pickups"); }}
                        href="#"
                      >
                        <i className="ri-truck-line me-1 align-bottom"></i>{" "}
                        Pickups{" "}
                        <span className="badge bg-danger align-middle ms-1">2</span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "4" }, "fw-semibold")}
                        onClick={() => { toggleTab("4", "Returns"); }}
                        href="#"
                      >
                        <i className="ri-arrow-left-right-fill me-1 align-bottom"></i>{" "}
                        Returns
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "5" }, "fw-semibold")}
                        onClick={() => { toggleTab("5", "Cancelled"); }}
                        href="#"
                      >
                        <i className="ri-close-circle-line me-1 align-bottom"></i>{" "}
                        Cancelled
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>
                <TableContainer
                  columns={columns}
                  data={orderList || []}
                  isGlobalFilter={true}
                  customPageSize={10}
                  className="mt-3"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
        <ModalHeader className="bg-light p-3" toggle={toggle}>
          {!!isEdit ? "Edit Order" : "Add Order"}
        </ModalHeader>
        <Form className="tablelist-form" onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="id-field" className="form-label">Order Id</Label>
              <Input
                name="orderId"
                id="id-field"
                className="form-control"
                placeholder="Enter Order Id"
                type="text"
                validate={{ required: { value: true } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.orderId || ""}
                invalid={validation.touched.orderId && validation.errors.orderId ? true : false}
                readOnly={isEdit}
              />
              {validation.touched.orderId && validation.errors.orderId ? (
                <FormFeedback type="invalid">{validation.errors.orderId}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="customername-field" className="form-label">Customer</Label>
              <Input
                name="customer"
                id="customername-field"
                className="form-control"
                placeholder="Enter Name"
                type="text"
                validate={{ required: { value: true } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.customer || ""}
                invalid={validation.touched.customer && validation.errors.customer ? true : false}
                readOnly={isEdit}
              />
              {validation.touched.customer && validation.errors.customer ? (
                <FormFeedback type="invalid">{validation.errors.customer}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="productname-field" className="form-label">Product</Label>
              <Input
                name="product"
                type="text"
                className="form-control"
                id="productname-field"
                placeholder="Enter Product"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.product || ""}
                invalid={validation.touched.product && validation.errors.product ? true : false}
                readOnly={isEdit}
              />
              {validation.touched.product && validation.errors.product ? (
                <FormFeedback type="invalid">{validation.errors.product}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="amount-field" className="form-label">Amount</Label>
              <Input
                name="amount"
                id="amount-field"
                className="form-control"
                placeholder="Enter Amount"
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.amount || ""}
                invalid={validation.touched.amount && validation.errors.amount ? true : false}
                readOnly={isEdit}
              />
              {validation.touched.amount && validation.errors.amount ? (
                <FormFeedback type="invalid">{validation.errors.amount}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="payment-field" className="form-label">Payment Method</Label>
              <Input
                name="payment"
                type="select"
                className="form-control"
                id="payment-field"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.payment || ""}
                invalid={validation.touched.payment && validation.errors.payment ? true : false}
                disabled={isEdit}
              >
                {orderpayement[0].options.map((item, key) => (
                  <option value={item.value} key={key}>{item.label}</option>
                ))}
              </Input>
              {validation.touched.payment && validation.errors.payment ? (
                <FormFeedback type="invalid">{validation.errors.payment}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="delivered-status" className="form-label">Delivery Status</Label>
              <Input
                name="status"
                type="select"
                className="form-control"
                id="delivered-status"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.status || ""}
                invalid={validation.touched.status && validation.errors.status ? true : false}
              >
                {orderstatus[0].options.map((item, key) => (
                  <option value={item.value} key={key}>{item.label}</option>
                ))}
              </Input>
              {validation.touched.status && validation.errors.status ? (
                <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
              ) : null}
            </div>

            {(validation.values.status === "Cancelled" || validation.values.status === "Returns") && (
              <div className="mb-3">
                <Label htmlFor="status-reason" className="form-label">Reason</Label>
                <Input
                  name="status_reason"
                  type="select"
                  className="form-control"
                  id="status-reason"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.status_reason || ""}
                  invalid={validation.touched.status_reason && validation.errors.status_reason ? true : false}
                >
                  <option value="">Select a reason</option>
                  {(validation.values.status === "Cancelled" ? cancelledReasons : returnedReasons).map((reason, key) => (
                    <option value={reason} key={key}>{reason}</option>
                  ))}
                </Input>
                {validation.touched.status_reason && validation.errors.status_reason ? (
                  <FormFeedback type="invalid">{validation.errors.status_reason}</FormFeedback>
                ) : null}
              </div>
            )}

            {(validation.values.status === "Cancelled" || validation.values.status === "Returns") && validation.values.status_reason === "Other (with comments)" && (
              <div className="mb-3">
                <Label htmlFor="status-reason-other" className="form-label">Comments</Label>
                <Input
                  name="status_reason_other"
                  type="textarea"
                  className="form-control"
                  id="status-reason-other"
                  placeholder="Enter custom reason or comments"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.status_reason_other || ""}
                  invalid={validation.touched.status_reason_other && validation.errors.status_reason_other ? true : false}
                />
                {validation.touched.status_reason_other && validation.errors.status_reason_other ? (
                  <FormFeedback type="invalid">{validation.errors.status_reason_other}</FormFeedback>
                ) : null}
              </div>
            )}
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-light" onClick={() => { setModal(false); }}>Close</button>
              <button type="submit" className="btn btn-success" id="add-btn">{!!isEdit ? "Update" : "Add Order"}</button>
            </div>
          </div>
        </Form>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default EcommerceOrders;
